import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartCard, EmptyState } from "./ChartCard";
import { Select } from "@/components/ui/Select";
import { chartTheme } from "./chartTheme";
import { useMetricsVolume } from "./useMetricsQueries";
import { useMetricsFilter } from "./MetricsFilterContext";
import type { VolumePeriod } from "@/api/dealFlow/metrics";

// Volume tab. One stacked bar chart that hosts all five series
// (Submitted / Approved / Declined / Withdrawn / Closed) with a
// monthly/quarterly/yearly period selector.

export function VolumeMetricsSection() {
  const { filter } = useMetricsFilter();
  const [period, setPeriod] = useState<VolumePeriod>("Monthly");
  const query = useMetricsVolume(filter, period);

  if (query.isLoading) return <Loading />;
  if (query.error || !query.data) return <Error />;

  const { buckets } = query.data;
  const empty = buckets.every(
    (b) =>
      b.submitted === 0 &&
      b.approved === 0 &&
      b.declined === 0 &&
      b.withdrawn === 0 &&
      b.closed === 0,
  );

  return (
    <div data-testid="metrics-loaded" className="flex flex-col gap-space-4">
      <div className="flex items-center justify-end gap-space-2">
        <label className="text-small text-gray-mid">Period</label>
        <Select
          value={period}
          onChange={(e) => setPeriod(e.target.value as VolumePeriod)}
          aria-label="Volume bucket period"
        >
          <option value="Monthly">Monthly</option>
          <option value="Quarterly">Quarterly</option>
          <option value="Yearly">Yearly</option>
        </Select>
      </div>
      <ChartCard
        title="Deal volume"
        hint="Submitted · Approved · Declined · Withdrawn · Closed by period."
        testId="chart-volume"
      >
        {empty ? (
          <EmptyState label="No volume in the selected window." />
        ) : (
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={buckets} margin={{ left: 16, right: 16 }}>
              <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
              <XAxis dataKey="label" stroke={chartTheme.axis} fontSize={11} />
              <YAxis stroke={chartTheme.axis} />
              <Tooltip />
              <Legend />
              <Bar dataKey="submitted" stackId="v" fill={chartTheme.info} />
              <Bar dataKey="approved" stackId="v" fill={chartTheme.success} />
              <Bar dataKey="declined" stackId="v" fill={chartTheme.error} />
              <Bar dataKey="withdrawn" stackId="v" fill={chartTheme.warning} />
              <Bar dataKey="closed" stackId="v" fill={chartTheme.primary} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </div>
  );
}

function Loading() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-gray-mid">
      Loading volume…
    </div>
  );
}

function Error() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-status-error">
      Failed to load volume metrics.
    </div>
  );
}
